/**
 * StrategyDetailView — the strategy detail page shared by every place a
 * full strategy is shown: the candidate detail page (Stage 1.4), the
 * locked view (Stage 1.5), and the reverse-engineered result (Stage 1.6).
 * `mode` drives which header controls render; everything else (overview,
 * pick accordion, ask bar) is identical.
 */

import { useState } from 'react'
import { V2, V2Card, PosChip, solidChip, pctTone, RefreshButton } from '../../components/JaiV2'
import { JAI_FONTS } from '../../components/JaiBrand'
import PlayerPhoto from '../../components/PlayerPhoto'
import StickyAskBar from '../../components/StickyAskBar'
import { teamColor } from '../../config/teamColors'
import { IS_NATIVE_APP } from '../../services/runtime'

const TAG_COLOR = { sleeper: V2.blue, safe: V2.success, bust: V2.error }
const TAG_LABEL = { sleeper: 'Sleeper', safe: 'Safe pick', bust: 'Bust risk' }
// JAI's researched read on a target (news, role change, injury), shown only on a
// plan being built for a draft still to come. Words, not emoji (Hard Rule 4).
const SIGNAL_LABEL = { riser: 'Rising', sleeper: 'Hidden gem', faller: 'Likely to slide', value: 'Value' }
// Same tag values, reworded for a review of picks already made — 'sleeper'/
// 'bust' describe future risk, which doesn't apply to a pick that's already
// locked into a roster.
const TAG_LABEL_REVIEW = { sleeper: 'Great value', safe: 'Fair pick', bust: 'Reach' }

function formatStatLine(stats, sport) {
  if (!stats) return null
  if (sport === 'nfl') {
    const parts = []
    if (stats.passYards) parts.push(`${stats.passYards} pass yds`)
    if (stats.rushYards) parts.push(`${stats.rushYards} rush yds`)
    if (stats.rushTD) parts.push(`${stats.rushTD} rush TD`)
    if (stats.receptions) parts.push(`${stats.receptions} rec`)
    if (stats.recYards) parts.push(`${stats.recYards} rec yds`)
    if (stats.recTD) parts.push(`${stats.recTD} rec TD`)
    return parts.join(' · ') || null
  }
  // One decimal: ESPN's projections arrive as 33.0629 and read as noise.
  const one = (n) => (Math.round(n * 10) / 10).toString()
  const parts = []
  if (stats.points != null) parts.push(`${one(stats.points)} pts`)
  if (stats.rebounds != null) parts.push(`${one(stats.rebounds)} reb`)
  if (stats.assists != null) parts.push(`${one(stats.assists)} ast`)
  if (stats.steals != null) parts.push(`${one(stats.steals)} stl`)
  if (stats.blocks != null) parts.push(`${one(stats.blocks)} blk`)
  // Only present at all for an NBA points-format league — see
  // draftStrategyService.js's nbaFantasyPtsPerGame.
  if (stats.fantasyPts != null) parts.push(`${one(stats.fantasyPts)} fpts/gm`)
  return parts.join(' · ') || null
}

function TeamChip({ abbr, sport }) {
  if (!abbr) return null
  const c = teamColor(abbr, sport)
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: c.ink, background: c.primary,
      borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap',
    }}>
      {abbr}
    </span>
  )
}

function PlayerRow({ target, sport, isReverse }) {
  const tagChip = solidChip(TAG_COLOR[target.tag] || V2.mute)
  const tagLabel = (isReverse ? TAG_LABEL_REVIEW : TAG_LABEL)[target.tag] || target.tag
  const projectedLine = formatStatLine(target.projected, sport)
  const lastSeasonLine = formatStatLine(target.lastSeason, sport)

  return (
    <div style={{ display: 'flex', gap: 14, padding: '14px 4px', borderBottom: `1px solid ${V2.hairSoft}` }}>
      <span style={{ fontSize: 15, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 22, flexShrink: 0 }}>
        {String(target.rank).padStart(2, '0')}
      </span>
      <PlayerPhoto name={target.name} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 800, color: V2.navy }}>{target.name}</span>
          {target.position && <PosChip>{target.position}</PosChip>}
          <TeamChip abbr={target.team} sport={sport} />
          {!isReverse && target.signal && SIGNAL_LABEL[target.signal] && (
            <span style={{ fontSize: 9.5, fontWeight: 800, ...solidChip(target.signal === 'faller' ? V2.error : V2.blue), borderRadius: 5, padding: '2px 6px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {SIGNAL_LABEL[target.signal]}
            </span>
          )}
          {target.updated && (
            <span style={{ fontSize: 9.5, fontWeight: 800, ...solidChip(V2.blue), borderRadius: 5, padding: '2px 6px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Updated
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 1, marginBottom: 8, borderRadius: 8, overflow: 'hidden', border: `1px solid ${V2.hairSoft}`, maxWidth: 320 }}>
          <div style={{ flex: 1, textAlign: 'center', padding: '5px 8px' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: isReverse ? V2.mute : pctTone(target.acquireProbability || 0), fontFamily: JAI_FONTS.mono }}>
              {isReverse ? 'Drafted' : `${Math.round((target.acquireProbability || 0) * 100)}% likely`}
            </span>
          </div>
          <div style={{ flex: 1, textAlign: 'center', padding: '5px 8px', ...tagChip }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: tagChip.color, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {tagLabel}
            </span>
          </div>
        </div>

        <div style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.8 }}>
          <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>ADP</strong> &nbsp;{target.adp?.consensus != null ? `${target.adp.consensus}${target.adp.sources?.[0]?.name ? ` · ${target.adp.sources[0].name}` : ''}` : '—'}</div>
          {projectedLine && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Projected</strong> &nbsp;{projectedLine}</div>}
          {lastSeasonLine && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Last year</strong> &nbsp;{lastSeasonLine}</div>}
          {!isReverse && target.evidence && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Intel</strong> &nbsp;{target.evidence}{target.expectedPick != null && target.adp?.consensus != null && ` Expected around pick ${Math.round(target.expectedPick)} (ADP ${target.adp.consensus}).`}</div>}
          {target.teamRole && <div><strong style={{ color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Role</strong> &nbsp;{target.teamRole}</div>}
        </div>
        {target.why && <div style={{ fontSize: 12.5, color: V2.blue, marginTop: 6, fontStyle: 'italic' }}>{target.why}</div>}
      </div>
    </div>
  )
}

// ── Non-card layouts (design options; `variant` on StrategyDetailView) ────────
// A solid rule marks what kind of note this is; the text stays dark and plain.
// No tinted fill, no border box.
function Rule({ color, label, children }) {
  return (
    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, borderRadius: 0, margin: '10px 0' }}>
      {label && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color, marginBottom: 3 }}>{label}</div>}
      <div style={{ fontSize: 13, lineHeight: 1.55, color: V2.navy }}>{children}</div>
    </div>
  )
}

const LABEL = { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase' }
const FACT = { color: V2.navy, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }
const likelyOf = (t) => Math.round((t.acquireProbability || 0) * 100)

/** The ADP / Projected / Last year / Role rows, shared by the plain layouts. */
function FactRows({ target, sport, isReverse }) {
  const projected = formatStatLine(target.projected, sport)
  const lastSeason = formatStatLine(target.lastSeason, sport)
  return (
    <div style={{ fontSize: 12.5, color: V2.mute, lineHeight: 1.8 }}>
      <div><strong style={FACT}>ADP</strong> &nbsp;{target.adp?.consensus != null ? `${target.adp.consensus}${target.adp.sources?.[0]?.name ? ` · ${target.adp.sources[0].name}` : ''}` : '—'}</div>
      {projected && <div><strong style={FACT}>Projected</strong> &nbsp;{projected}</div>}
      {lastSeason && <div><strong style={FACT}>Last year</strong> &nbsp;{lastSeason}</div>}
      {!isReverse && target.evidence && <div><strong style={FACT}>Intel</strong> &nbsp;{target.evidence}</div>}
      {target.teamRole && <div><strong style={FACT}>Role</strong> &nbsp;{target.teamRole}</div>}
    </div>
  )
}

function NameLine({ target, sport, isReverse }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 14.5, fontWeight: 800, color: V2.navy }}>{target.name}</span>
      {target.position && <PosChip>{target.position}</PosChip>}
      <TeamChip abbr={target.team} sport={sport} />
      {!isReverse && target.signal && SIGNAL_LABEL[target.signal] && (
        <span style={{ fontSize: 11, fontWeight: 700, color: target.signal === 'faller' ? V2.error : V2.blue }}>{SIGNAL_LABEL[target.signal]}</span>
      )}
    </div>
  )
}

/** C: the likelihood and the tag as one line of coloured text, the reasoning as a margin rule. */
function PlayerRowC({ target, sport, isReverse }) {
  const tagLabel = (isReverse ? TAG_LABEL_REVIEW : TAG_LABEL)[target.tag] || target.tag
  return (
    <div style={{ display: 'flex', gap: 14, padding: '16px 0', borderTop: `1px solid ${V2.hairSoft}` }}>
      <span style={{ fontSize: 15, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono, width: 22, flexShrink: 0 }}>{String(target.rank).padStart(2, '0')}</span>
      <PlayerPhoto name={target.name} size={40} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <NameLine target={target} sport={sport} isReverse={isReverse} />
        <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 12.5, margin: '5px 0 8px' }}>
          <span style={{ color: isReverse ? V2.mute : pctTone(target.acquireProbability || 0), fontWeight: 700 }}>{isReverse ? 'Drafted' : `${likelyOf(target)}% likely`}</span>
          <span style={{ color: V2.faint }}> · </span>
          <span style={{ color: TAG_COLOR[target.tag] || V2.mute, fontWeight: 700 }}>{tagLabel}</span>
        </div>
        <FactRows target={target} sport={sport} isReverse={isReverse} />
        {target.why && <Rule color={V2.blue}>{target.why}</Rule>}
      </div>
    </div>
  )
}

/** D: a ledger. One line per player in columns, notes underneath, nothing boxed. */
function PlayerRowD({ target, sport, isReverse }) {
  const tagLabel = (isReverse ? TAG_LABEL_REVIEW : TAG_LABEL)[target.tag] || target.tag
  const adp = target.adp?.consensus
  return (
    <div style={{ padding: '12px 0', borderTop: `1px solid ${V2.hairSoft}` }}>
      <div style={{ display: 'grid', gridTemplateColumns: '24px minmax(0,1fr) 96px 92px 56px', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: V2.faint, fontFamily: JAI_FONTS.mono }}>{String(target.rank).padStart(2, '0')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <PlayerPhoto name={target.name} size={30} />
          <NameLine target={target} sport={sport} isReverse={isReverse} />
        </div>
        <span style={{ fontFamily: JAI_FONTS.mono, fontSize: 12.5, fontWeight: 700, textAlign: 'right', color: isReverse ? V2.mute : pctTone(target.acquireProbability || 0) }}>{isReverse ? 'Drafted' : `${likelyOf(target)}% likely`}</span>
        <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'right', color: TAG_COLOR[target.tag] || V2.mute }}>{tagLabel}</span>
        <span style={{ fontFamily: JAI_FONTS.mono, fontSize: 12.5, textAlign: 'right', color: V2.mute }}>{adp != null ? adp : '—'}</span>
      </div>
      {(target.why || target.evidence) && (
        <div style={{ marginLeft: 34 }}>
          <Rule color={V2.blue}>{target.why || target.evidence}</Rule>
        </div>
      )}
    </div>
  )
}

/** E: each pick reads as a short paragraph. The facts sit inline; the numbers go in one mono line. */
function PlayerRowE({ target, sport, isReverse }) {
  const tagLabel = (isReverse ? TAG_LABEL_REVIEW : TAG_LABEL)[target.tag] || target.tag
  const projected = formatStatLine(target.projected, sport)
  const adp = target.adp?.consensus
  return (
    <div style={{ display: 'flex', gap: 14, padding: '14px 0', borderTop: `1px solid ${V2.hairSoft}` }}>
      <PlayerPhoto name={target.name} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <NameLine target={target} sport={sport} isReverse={isReverse} />
        <p style={{ fontSize: 13.5, lineHeight: 1.7, color: V2.navy, margin: '4px 0 0' }}>
          {isReverse ? 'Drafted' : <><strong style={{ fontWeight: 700, color: pctTone(target.acquireProbability || 0) }}>{likelyOf(target)}% likely</strong> to still be there</>}
          {adp != null && <>, ADP {adp}</>}
          {'. '}
          <span style={{ color: TAG_COLOR[target.tag] || V2.mute, fontWeight: 700 }}>{tagLabel}.</span>
          {target.why ? ` ${target.why}` : ''}
        </p>
        {(projected || target.teamRole) && (
          <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 11.5, color: V2.mute, marginTop: 3 }}>
            {[projected && `Proj ${projected}`, target.teamRole].filter(Boolean).join('  ·  ')}
          </div>
        )}
      </div>
    </div>
  )
}

const PLAIN_ROW = { c: PlayerRowC, d: PlayerRowD, e: PlayerRowE }

/** A round with no card around it: a ruled heading, the players beneath. */
function PickSection({ pick, sport, defaultOpen, isReverse, variant }) {
  const [open, setOpen] = useState(defaultOpen)
  const Row = PLAIN_ROW[variant] || PlayerRowC
  return (
    <div style={{ marginBottom: 14 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', background: 'none', border: 'none', borderBottom: `2px solid ${V2.navy}`, cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.navy, fontFamily: JAI_FONTS.mono }}>
          Round {pick.round} · Pick {pick.pickInRound} · Overall {pick.overallPick}
          {pick.extraPicks?.length ? ` · and a second pick at Overall ${pick.extraPicks.map((e) => e.overallPick).join(', ')}` : ''}
        </span>
        <span style={{ color: V2.faint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>&#9662;</span>
      </button>
      {open && pick.targets.map((t, i) => <Row key={i} target={t} sport={sport} isReverse={isReverse} />)}
    </div>
  )
}

/** The overview as plain figures with hairlines between them (no outlined box). */
function PlainOverview({ strategy, isReverse }) {
  const Fig = ({ label, value, tone, sub }) => (
    <div style={{ minWidth: 120 }}>
      <div style={LABEL}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: tone, fontFamily: JAI_FONTS.mono, lineHeight: 1.15, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: V2.mute, marginTop: 2, maxWidth: 190 }}>{sub}</div>}
    </div>
  )
  return (
    <div style={{ display: 'flex', gap: 36, flexWrap: 'wrap', padding: '6px 0 18px', borderBottom: `1px solid ${V2.hairSoft}`, marginBottom: 18 }}>
      <div style={{ flex: '1 1 200px', minWidth: 160 }}>
        <div style={LABEL}>Type of players</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: V2.navy, marginTop: 6, lineHeight: 1.4 }}>{strategy.playerType || '—'}</div>
      </div>
      <Fig label={isReverse ? 'Reach rate' : 'Risk'} value={`${strategy.riskPercent}%`} tone={pctTone(strategy.riskPercent, { invert: true })}
        sub={isReverse ? `${strategy.sleeperCount} great value · ${strategy.safeCount} fair · ${strategy.bustCount} reaches` : `${strategy.sleeperCount} sleepers · ${strategy.safeCount} safe · ${strategy.bustCount} busts`} />
      {!isReverse && <Fig label="Targets available" value={`${strategy.successProbability}%`} tone={pctTone(strategy.successProbability)} sub="chance a listed target is there at each pick" />}
    </div>
  )
}

function PickRow({ pick, sport, defaultOpen, isReverse }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <V2Card pad="0" style={{ marginBottom: 10, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.navy, fontFamily: JAI_FONTS.mono }}>
          Round {pick.round} · Pick {pick.pickInRound} · Overall {pick.overallPick}
          {pick.extraPicks?.length ? ` · and a second pick at Overall ${pick.extraPicks.map((e) => e.overallPick).join(', ')}` : ''}
        </span>
        <span style={{ color: V2.faint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>&#9662;</span>
      </button>
      {open && (
        <div style={{ padding: '0 18px 6px' }}>
          {pick.targets.map((t, i) => <PlayerRow key={i} target={t} sport={sport} isReverse={isReverse} />)}
        </div>
      )}
    </V2Card>
  )
}

function LockedRoundRow({ round }) {
  return (
    <V2Card pad="14px 18px" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0.7 }}>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: V2.mute, fontFamily: JAI_FONTS.mono }}>Round {round}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: V2.blue }}>
        {IS_NATIVE_APP ? 'Not on your plan' : <>Upgrade to unlock &rarr;</>}
      </span>
    </V2Card>
  )
}

export default function StrategyDetailView({
  league, strategy, mode = 'candidate', totalRounds, variant = 'c',
  onConfirm, onChangeStrategy, onEditDetails, onPickTrades, onRefresh, refreshing, confirming, onBack,
}) {
  if (!strategy) return null
  const isReverse = mode === 'reverse'
  const sport = (league?.sport || 'nba').toLowerCase()
  const maxShownRound = strategy.picks.length ? Math.max(...strategy.picks.map((p) => p.round)) : 0
  const lockedRounds = totalRounds && totalRounds > maxShownRound
    ? Array.from({ length: totalRounds - maxShownRound }, (_, i) => maxShownRound + i + 1)
    : []

  return (
    <div style={{ paddingBottom: 100 }}>
      {mode === 'candidate' && onBack && (
        <button
          onClick={onBack}
          style={{ background: 'none', border: 'none', padding: 0, marginBottom: 10, color: V2.blue, fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}
        >
          &larr; All strategies
        </button>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: mode === 'candidate' ? 22 : 8, gap: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 800, color: V2.navy }}>{strategy.name}</span>
        {mode === 'candidate' && (
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{ background: V2.blue, color: 'white', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, cursor: confirming ? 'default' : 'pointer', opacity: confirming ? 0.6 : 1 }}
          >
            {confirming ? 'Locking…' : 'Confirm'}
          </button>
        )}
      </div>

      {(mode === 'locked' || isReverse) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 18, fontSize: 12, color: V2.mute }}>
          <span>
            {isReverse
              ? 'Your actual draft — reviewed after the fact, not JAI recommendations'
              : `Locked${strategy.lockedAt ? ` · ${new Date(strategy.lockedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}`}
          </span>
          {!isReverse && strategy.refreshedAt && <span>· Updated {new Date(strategy.refreshedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>}
          {onChangeStrategy && <button onClick={onChangeStrategy} style={{ background: 'none', border: 'none', color: V2.blue, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>{isReverse ? 'Re-analyse' : 'Change strategy'}</button>}
          {onRefresh && (
            <RefreshButton onClick={onRefresh} loading={refreshing} style={{ fontSize: 12, padding: '4px 11px' }} />
          )}
        </div>
      )}

      {mode === 'locked' && onEditDetails && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginTop: -6, marginBottom: 18 }}>
          {onPickTrades && (
            <button
              type="button" data-testid="pick-trades-open"
              onClick={onPickTrades}
              style={{ minHeight: 52, padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, border: `1px solid ${V2.hairSoft}`, background: 'white', color: V2.navy, cursor: 'pointer' }}
            >
              Pick trades
            </button>
          )}
          <button
            type="button" data-testid="edit-league-details"
            onClick={onEditDetails}
            style={{ minHeight: 52, padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700, border: `1px solid ${V2.hairSoft}`, background: 'white', color: V2.navy, cursor: 'pointer' }}
          >
            Edit league details
          </button>
        </div>
      )}

      {variant !== 'current' ? <PlainOverview strategy={strategy} isReverse={isReverse} /> : (
      <div style={{ display: 'flex', gap: 1, borderRadius: 12, overflow: 'hidden', border: `1px solid ${V2.hairSoft}`, marginBottom: 26 }}>
        <div style={{ flex: 1, padding: '14px 10px', textAlign: 'center', background: 'white' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 6 }}>Type of Players</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: V2.navy }}>{strategy.playerType || '—'}</div>
        </div>
        <div style={{ flex: 1, padding: '14px 10px', textAlign: 'center', background: 'white' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 6 }}>{isReverse ? 'Reach Rate' : 'Risk Level'}</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: pctTone(strategy.riskPercent, { invert: true }), fontFamily: JAI_FONTS.mono }}>{strategy.riskPercent}%</div>
          <div style={{ fontSize: 10.5, color: V2.mute, marginTop: 2 }}>
            {isReverse
              ? `${strategy.sleeperCount} great value · ${strategy.safeCount} fair · ${strategy.bustCount} reaches`
              : `${strategy.sleeperCount} sleepers · ${strategy.safeCount} safe · ${strategy.bustCount} busts`}
          </div>
        </div>
        {!isReverse && (
          <div style={{ flex: 1, padding: '14px 10px', textAlign: 'center', background: 'white' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: V2.faint, textTransform: 'uppercase', marginBottom: 6 }}>Targets Available</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: pctTone(strategy.successProbability), fontFamily: JAI_FONTS.mono }}>{strategy.successProbability}%</div>
            {/* This is the chance one of the round's listed targets is still on the board at each pick, not a promise the plan wins. */}
            <div style={{ fontSize: 10.5, color: V2.mute, marginTop: 2 }}>chance a listed target is there at each pick</div>
          </div>
        )}
      </div>
      )}

      {strategy.rationale && (
        <p style={{ fontSize: 13, color: V2.mute, lineHeight: 1.6, marginBottom: (strategy.positionalRunWarning || strategy.settingsWarning) ? 10 : 22 }}>{strategy.rationale}</p>
      )}

      {variant !== 'current' && strategy.settingsWarning && <Rule color={V2.warning} label="Check your league details">{strategy.settingsWarning}</Rule>}
      {variant !== 'current' && strategy.positionalRunWarning && <Rule color={V2.warning} label="Watch">{strategy.positionalRunWarning}</Rule>}


      {strategy.picks.map((pick, i) => (variant === 'current'
        ? <PickRow key={pick.overallPick} pick={pick} sport={sport} defaultOpen={i === 0} isReverse={isReverse} />
        : <PickSection key={pick.overallPick} pick={pick} sport={sport} defaultOpen={i === 0} isReverse={isReverse} variant={variant} />
      ))}
      {lockedRounds.map((round) => <LockedRoundRow key={round} round={round} />)}

      {mode !== 'candidate' && (
        <StickyAskBar leagueId={league.id} scope="strategy" scopeId={strategy.id} placeholder="Ask JAI about this strategy" />
      )}
    </div>
  )
}
